import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, ImagePlus } from 'lucide-react'
import { createClient, getClient, updateClient } from '../api/clients'
import { uploadImage } from '../api/upload'
import { useAuth } from '../context/AuthContext'
import styles from './ClientForm.module.css'

export default function ClientForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const { token, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const fileRef = useRef(null)

  const [fetching, setFetching] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [thumbnail, setThumbnail] = useState('')
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    if (!isEdit || !token) return
    getClient(token, id)
      .then((c) => {
        reset({ name: c.name, email: c.email, contact_number: c.contact_number, website_address: c.website_address })
        setThumbnail(c.thumbnail || '')
      })
      .catch(() => setError('Client not found.'))
      .finally(() => setFetching(false))
  }, [id, token, isEdit, reset])

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const url = await uploadImage(file, token)
      setThumbnail(url)
    } catch {
      setError('Image upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = async (data) => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: data.name,
        email: data.email,
        contact_number: data.contact_number,
        website_address: data.website_address,
        thumbnail,
      }
      if (isEdit) {
        await updateClient(token, id, payload)
      } else {
        await createClient(token, payload)
      }
      navigate('/clients')
    } catch (err) {
      const msgs = err.response?.data?.errors
      setError(msgs ? msgs.join(', ') : `Failed to ${isEdit ? 'save' : 'create'} client.`)
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || !user) return null
  if (fetching) return <div style={{ padding: '48px 24px', color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <main className={styles.page}>
      <Link to="/clients" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Clients
      </Link>
      <div className={styles.card}>
        <h1 className={styles.title}>{isEdit ? 'Edit Client' : 'New Client'}</h1>
        <p className={styles.subtitle}>
          {isEdit ? 'Update client information.' : 'Add a new client to your list.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.grid}>
            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Thumbnail</label>
              {thumbnail && (
                <img src={thumbnail} alt="Thumbnail preview" className={styles.thumbnailPreview} />
              )}
              <button
                type="button"
                className={styles.uploadBtn}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                <ImagePlus size={15} />
                {uploading ? 'Uploading…' : thumbnail ? 'Change Photo' : 'Upload Photo'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className={styles.fileInput}
                onChange={handleFileChange}
              />
            </div>

            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Full Name *</label>
              <input
                className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                placeholder="e.g. John Doe"
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <p className={styles.fieldError}>{errors.name.message}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input
                type="email"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                placeholder="e.g. john@example.com"
                {...register('email', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {errors.email && <p className={styles.fieldError}>{errors.email.message}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Contact Number</label>
              <input
                className={styles.input}
                placeholder="e.g. +1 555 000 0000"
                {...register('contact_number')}
              />
            </div>

            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Website Address</label>
              <input
                type="url"
                className={`${styles.input} ${errors.website_address ? styles.inputError : ''}`}
                placeholder="e.g. https://example.com"
                {...register('website_address', {
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: 'Must start with http:// or https://',
                  },
                })}
              />
              {errors.website_address && (
                <p className={styles.fieldError}>{errors.website_address.message}</p>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={saving || uploading}>
              {saving
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save Changes' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}
